import cx from 'classnames';
import Dropdown from 'rc-dropdown';
import CommonIcon from '../CommonIcon';
import styles from './index.module.scss';
import { useTranslations } from 'next-intl';
import { ChannelSortType, TopLevelChannels } from '@/api/home';
import Menu, { Item as MenuItem } from 'rc-menu';
import  { IMenuListItem } from '../CommonDropdown/mIndex';
import ArrowDown from '../../assets/icon/arrowDownActive.svg';
import SkeletonFeedContent from '../Skeleton/SkeletonFeedContent';
import { SortFilter, SortFilteTypeEnum } from '../SortFilter/mIndex';
import React, { useState, useEffect, useRef, FC, useMemo, useContext } from 'react';
import { StoreContext, namespace, ActionTypeValues } from '@/context';
import Image from 'next/image';
import { Router, useRouter } from 'next/router';

// 页面Header的固定高度 64px
const PageHeaderHeight = 64;

// tab option
export interface Tab {
  key: string;
  tabName: string;
  orderRule: ChannelSortType[];
}

/** 请求内容接口参数 */
export interface FetchParams {
  /** 当前tab的key */
  tabKey: string,
  /** 排序下拉选项的key */
  sortKey: string,
  /** 请求参数offset */
  offset?: number,
  /** 请求参数pageSize */
  pageSize: number,
}

// tab 搜索组件的属性
interface TabSearchComponentProps<T> {
  /** id */
  id: keyof T;
  /** 默认展示内容(ssr内容) */
  defaultContent?: T[];
  /** tab option */
  tabs: Tab[];
  /** 内容组件 */
  Component: FC<T>;
  /** 更多tabs */
  moreTabs?: Tab[];
  /** more desc */
  moreLabel?: string;
  /** 每页请求数目 */
  pageSize?: number;
  /** 记录每个tab的专属排序下拉选项 */
  tabSortFilterMap: Record<string, IMenuListItem[]>;
  /** 区分模块（TopLevelChannels） */
  channel?: TopLevelChannels;
  /** 请求 */
  fetchApi: (params: FetchParams) => Promise<T[]>;
}

// feed流 tab搜索组件
const TabSearchComponent = <T extends {}>({
  id,
  moreLabel,
  Component,
  tabs = [],
  moreTabs = [],
  pageSize = 10,
  defaultContent,
  tabSortFilterMap,
  channel,
  fetchApi,
}: TabSearchComponentProps<T>) => {
  const t = useTranslations();
  const { state, dispatch } = useContext(StoreContext);
  const { track, TRACK_EVENTS } = state[namespace.app].sensors || {};

  // 记录首屏 / 首次数据是否已经上报过
  const firstPageTrackedRef = useRef<Record<string, boolean>>({});

  // 路由实例
    const router = useRouter();

  const { pathname } = useRouter();

  // 用于记录访问过的tab 内容区域
  const contentRef = useRef<HTMLDivElement | null>(null);
  /** 当前排序类型 */
  const [sortKey, setSortKey] = useState<string>('');
  // 当前激活的tab标签
  const [activeTab, setActiveTab] = useState<string>('');
  // 记录请求过的数据
  const [content, setContent] = useState<Record<string, {data: T[], isLoaded: boolean}>>({});
  // 记录各个tab，滚动的高度，实现 keep-alive
  const [scrollPositions, setScrollPositions] = useState<Record<string, number>>({});
  /** 下拉选项tab */
  const [dropdownMenuValue, setDropDownMenuValue] = useState('');
  /** 加载全部 */
  const [loaded, setLoaded] = useState(false);
  /** 首次加载标识 */
  const isFirstRender = useRef(true);
  /** 请求中状态，避免重复请求 */ 
  const isFetching = useRef(false);
  /** 骨架屏 loading */
  const [loading, setLoading] = useState(false);
  /** 滚动 loading */
  const [scrollLoading, setScrollLoading] = useState(false);
  /** nextElementRef */
  const nextElementRef = useRef<HTMLElement | null>(null);
  /** tabHeaderRef */
  const tabHeaderRef = useRef<HTMLDivElement | null>(null);
  /** 记录每一次切换tab前的scrollTop */
  const prevTabScrollTop = useRef<number>(0);
  /** tabHeader 容器距离视窗顶部的高度 */
  const [tabHeaderOffsetTop, setTabHeaderOffsetTop] = useState(PageHeaderHeight);
  /** 记录tab当前的sort值 */
  const [tabSortCache, setTabSortCache] = useState<Record<string, ChannelSortType>>({});
  /** 记录每个tab的当前offset */
  const tabOffsetCache = useRef<Record<string, number>>({}); 
  /* 当前激活的tab标签name */
  const activeTabName = useRef('');
  /** 控制滚动loading展示 */
  const [showLoading, setShowLoading] = useState(false);


  useEffect(() => {
    nextElementRef.current = document.querySelectorAll('#__next')?.[0] as HTMLElement | null;

    if (tabHeaderRef.current && isFirstRender.current) {
      // 获取tab 到顶部的距离
      setTabHeaderOffsetTop(tabHeaderRef.current.getBoundingClientRect().top)
    }

    if (tabs.length && !activeTab) {
      setActiveTab(tabs[0].key);
      setSortKey(tabs[0].orderRule[0] || '');
    }
  }, [tabs]);

  useEffect(() => {
        console.log("🚀 ~ Search ~ state:", defaultContent, !firstPageTrackedRef.current[activeTab],activeTab, track)
    if (!defaultContent) return;

    if (!firstPageTrackedRef.current[activeTab] && track) {
      track?.(
        TRACK_EVENTS?.[namespace.search]?.searchResultShow?.name,
        {
          search_term: router.query.keyword as string,
          is_recall: defaultContent.length === 0 ? 0 : 1,
          search_result_count: defaultContent.length,
        },
        namespace.search,
      );
      firstPageTrackedRef.current[activeTab] = true;
    }
  }, [defaultContent, activeTab, track, router.query.keyword]);

  // 切换tab标签触发请求
  useEffect(() => {
    // 如果第一次加载组件且有传入默认内容，就不主动请求接口
    if (isFirstRender.current && defaultContent) {
      setContent({[activeTab]: {
        data: defaultContent,
        isLoaded: false
      }});
      tabOffsetCache.current = {
        [activeTab]: defaultContent.length,
      };
      activeTabName.current = [...tabs, ...moreTabs].find((item) => item.key === activeTab)?.tabName || '';
      // 更新记录在全局的"已激活的tab标签name"值
      dispatch({
        namespace: namespace.home,
        type: ActionTypeValues.UPDATE_PC_HOME_ACTIVE_CHANNEL_NAME,
        payload: activeTabName.current,
      })
      return
    }
    if (!content[activeTab]) {
      setLoading(true);
      fetchContent(activeTab).finally(() => {setLoading(false)})
    }
  }, [activeTab, defaultContent]);

  // 请求数据
  const fetchContent = (tabKey: string, append: boolean = false) => { // append = true 为懒加载追加内容的标识
    if (loaded || isFetching.current) {
      return Promise.resolve();
    }
    isFetching.current = true;
    return fetchApi({
      tabKey,
      sortKey,
      pageSize,
      offset: append && content[tabKey] ? tabOffsetCache.current[tabKey] : 0, // 滚动懒加载且当前tab有缓存数据，则按照现有数据量继续请求
    }).then((res) => {

        console.log("🚀 ~ Search ~ state:", res)
      
      if (!res.length && !loaded) {
        setLoaded(true)
      }
      setContent((prevContent) => {
        const currentContent = prevContent[tabKey]?.data || [];
        let newContent;
  
        if (append) {
          newContent = [...currentContent, ...res];
        } else {
          newContent = res;
        }
  
        // 使用哈希表进行去重
        const seen = new Set();
        const uniqueContent = newContent.filter(item => {
          if (!seen.has(item[id])) {
            seen.add(item[id]);
            return true;
          }
          return false;
        });
  
        return {
          ...prevContent,
          [tabKey]: {
            data: uniqueContent,
            isLoaded: !res.length && !loaded,
          },
        };
      });
      tabOffsetCache.current[tabKey] = (tabOffsetCache.current[tabKey] ?? 0) + pageSize;
      
      if (!firstPageTrackedRef.current[tabKey]) {
        track?.(
          TRACK_EVENTS?.[namespace.search]?.searchResultShow?.name,
          {
            search_term: router.query.keyword as string,
            is_recall: res.length === 0 ? 0 : 1,
            search_result_count: res.length,
          },
          namespace.search,
        );
        firstPageTrackedRef.current[tabKey] = true;
      }
      
    }).finally(() => {
      
        console.log("🚀 ~ Search ~ state:", 11212)
      isFetching.current = false;
      setScrollLoading(false);
    })
  };

 // 切换tab标签
  const handleTabClick = (key: string, text: string) => {
    isFirstRender.current = false; // 避免开发模式下React严格模式会让useEffect执行两次导致首屏内容渲染有误
    if (activeTab !== key) {
      if (content[key] && content[key].isLoaded) {
        setLoaded(true);
      } else {
        setLoaded(false);
      }
      const scrollTop = nextElementRef.current?.scrollTop || 0; // 获取当前滚动位置
      prevTabScrollTop.current = scrollTop;
      setScrollPositions((prevPositions) => ({
        ...prevPositions,
        [activeTab]: scrollTop, // 记录当前 tab 的滚动位置
      }));
      track?.(TRACK_EVENTS?.[namespace.home].selectTopListClick.name, {
        button_name: text,
        list_name: activeTabName.current,
      }, namespace.home);
      // 更新记录在全局的"已激活的tab标签name"值
      dispatch({
        namespace: namespace.home,
        type: ActionTypeValues.UPDATE_PC_HOME_ACTIVE_CHANNEL_NAME,
        payload: text,
      })
      activeTabName.current = text;
      setActiveTab(key);
      if (tabSortFilterMap[key].length > 1 && tabSortCache[key]) {
        setSortKey(tabSortCache[key]);
        return;
      }
      setSortKey(tabSortFilterMap[key][0].key || '');
    }
  };

// 滚动事件中只处理内容加载
const handleScroll = () => {
  if (contentRef.current && nextElementRef.current) {
    const { scrollHeight } = contentRef.current;
    const scrollTop = nextElementRef.current.scrollTop;
    const clientHeight = window.innerHeight;
    const contentTop = contentRef.current.getBoundingClientRect().top + scrollTop;

    // 计算 2/3 的高度
    const triggerHeight = contentTop + scrollHeight * (2 / 3);

    // 当滚动到页面的 2/3 处时触发请求
    if (scrollTop + clientHeight >= triggerHeight) {
      if (loaded) { // 如果当前的 tab 已经完全加载完成了，不重新触发 fetch
        return;
      }
      setScrollLoading(true);
      fetchContent(activeTab, true);
    }
  }
};

// 在组件挂载和卸载时添加和移除 window 的滚动事件监听器
useEffect(() => {
  nextElementRef.current?.addEventListener('scroll', handleScroll);

  return () => {
    nextElementRef.current?.removeEventListener('scroll', handleScroll);
  }
}, [activeTab, content, loaded])

// 切换 tab 时恢复滚动位置
useEffect(() => {
  if (nextElementRef.current) {
    let targetTabInitPosition = 0;
    if (scrollPositions[activeTab]) {
      // 如果当前tab有访问过（已经缓存了位置，直接）
      targetTabInitPosition = scrollPositions[activeTab]
    } else { // 如果没有访问过，页面的滚动距离由上一个tab的滚动距离决定
      // tabHeader吸顶的滚动距离 = 距离视窗顶部的距离 - 页面Header的固定高度
      const tabStickyScrollTop  = tabHeaderOffsetTop - PageHeaderHeight
      targetTabInitPosition = (prevTabScrollTop.current >= tabStickyScrollTop) ? tabStickyScrollTop : prevTabScrollTop.current;
    }
    // 设置当前tab的滚动位置
    nextElementRef.current.scrollTop = targetTabInitPosition;
  }
}, [activeTab, scrollPositions, tabHeaderOffsetTop]);

  // 下拉选项tab激活
  const moreActive = () => {
    return activeTab === dropdownMenuValue;
  }

  useEffect(() => {
    let timer:  NodeJS.Timeout | undefined;
  
    if (scrollLoading) {
      // 如果 scrollLoading 为 true，设置一个 500ms 的定时器
      timer = setTimeout(() => {
        setShowLoading(true);
      }, 500);
    } else {
      // 如果 scrollLoading 变为 false，清除定时器并隐藏 loading
      setShowLoading(false);
      clearTimeout(timer);
    }
  
    // 清理函数，组件卸载时清除定时器
    return () => clearTimeout(timer);
  }, [scrollLoading]);

  // 排序下拉选项tabs菜单
  const menu = (
    <Menu
      defaultValue={[]}
      activeKey={dropdownMenuValue}
      className={cx(styles.dropdownMenu)}
    >
      {
        moreTabs?.map(item => (
          <MenuItem
            key={item.key}
            onClick={() => {
              handleTabClick(item.key, item.tabName);
              setDropDownMenuValue(item.key);
            }}
            className={cx(styles.dropdownMenuItem, activeTab === item.key ? styles.active : '')}
          >
            {item.tabName}
          </MenuItem>
        )
      )}
    </Menu>
  )
  // 使用 useMemo 缓存内容组件，避免不必要的重新渲染
  const renderedContent = useMemo(() => {
    return [...tabs, ...moreTabs].map((tab) => (
      <div
        key={tab.key}
        style={{display: activeTab === tab.key ? 'flex' : 'none', }}
        ref={activeTab === tab.key ? contentRef : null}
        className={cx(styles.tabContent,
          channel === TopLevelChannels.home_page_consult_channel  ? styles.consultTabContent : '',
          content[tab.key]?.data.length === 0 && styles.emptyContent,
          channel === TopLevelChannels.search_result_channel ? styles.searchTabContent : '')}
      >
        {
          content[tab.key]?.data.map((item) => (
            <div key={`${item[id]}${tab.key}`} className={styles.contentItem}>
              <Component {...item} />
            </div>
          ))
        }
        {
          (showLoading && !loaded) && (
            <div className={styles.bottom}>
              <CommonIcon icon='loading-white' className={styles.loading} />
            </div>
          )
        }

        {/* 空状态处理：
          1. 如果当前tab没有数据，且频道是搜索结果页，则展示空页面
          2. 如果当前tab没有数据，且频道不是搜索结果页，则展示没有更多数据
        */}
        {
        content[tab.key]?.data.length === 0 && channel === TopLevelChannels.search_result_channel ? (
          <div className={styles.emptyWrapper}>
            <CommonIcon icon='empty-image' className={styles.emptyImg}/>
            {
                <>
                  <p className={styles.title}>{t('search_result_empty_title')}</p>
                  <p className={styles.subTitle}>{t('search_result_empty_subtitle')}</p>
                </>
            }
          </div>
        ) :
        loaded && <div className={styles.loaded}>{t('no_more_data')}</div>
      }

      </div>
    ));
  }, [activeTab, content, loaded, scrollLoading]);

  // 监听路由变化，当路由变化时，重置loaded状态
  useEffect(() => {
    const handleRouteChange = () => {
      if (pathname.includes('search')) {
        setLoaded(false);
      }
    };

    Router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      Router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  return (
    <div className={styles.tabComponent}>
      <div className={styles.tabHeader} ref={tabHeaderRef}>
        <div className={styles.tabs}>
          {/* 优先展示tabs */}
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.tabButton} ${activeTab === tab.key ? styles.active : ''}`}
              onClick={() => handleTabClick(tab.key, tab.tabName)}
            >
              {tab.tabName}
            </button>
          ))}
          {/* 下拉选项tabs */}
          {!!moreTabs.length && (
              <Dropdown
                overlay={menu}
                trigger="hover"
                placement='bottom'
                getPopupContainer={triggerNode => triggerNode.parentNode as HTMLElement}
              >
                <button
                  className={cx(styles.tabButton, styles.dropdownButton, moreActive() ? styles.active : '')}
                >
                  {moreLabel || '更多' }
                  {moreActive() ? <ArrowDown /> : <div className={styles.expandIcon}/>}
                </button>
              </Dropdown>
            )
          }
        </div>
        {/* 排序下拉 */}
        {
          (activeTab && tabSortFilterMap[activeTab] && tabSortFilterMap[activeTab].length > 1) && (
            <SortFilter
              activeSort={sortKey}
              className={styles.sortFilter}
              type={SortFilteTypeEnum.dropdown}
              sortList={tabSortFilterMap[activeTab]}
              onChange={(key) => {
                setLoaded(false);
                setSortKey(key);
                setTabSortCache({...tabSortCache, [activeTab]: key as unknown as ChannelSortType});
                setScrollPositions({...scrollPositions, [activeTab]: 0});
                setLoading(true);
                
                // 排序选项修改重新请求数据
                fetchApi({
                  pageSize,
                  offset: 0,
                  sortKey: key,
                  tabKey: activeTab,
                }).then((res) => {
                  if (!res.length) {
                    setLoaded(true);
                  }
                  // 更新内容的同时更新 offset
                  setContent((prevContent) => ({
                    ...prevContent,
                    [activeTab]: {
                      data: res,
                      isLoaded: !res.length,
                    },
                  }));
                  // 在新数据加载完成后再更新 offset
                  tabOffsetCache.current = {
                    ...tabOffsetCache.current,
                    [activeTab]: res.length  // 使用实际加载的数据长度
                  };
                }).finally(() => setLoading(false));
              }} 
            />
          )
        }
      </div>
      <div style={loading ? {display: 'flex'} : { display: 'none' }} className={styles.spinnerContainer}>
        <SkeletonFeedContent channel={channel!} />
      </div>
      {/* 渲染内容容器 */}
      {renderedContent}
    </div>
  );
};

export default TabSearchComponent;
